import { getChannelId, getOperationName } from "@/apiUtils/commonUtils";
import { NextOptions, TypeFetchCache } from "../common/lib/types";

/**
 * Opt-in default for call sites that explicitly want a bounded timeout
 * instead of hanging indefinitely (see MLC-2522). Not applied automatically —
 * most queries in this app have no inherent upper bound and must not time out.
 */
export const DEFAULT_REQUEST_TIMEOUT_MS = 15000;

/** Ceiling enforced on the Sanity-configured checkout timeout (MLC-2522). */
export const MAX_CHECKOUT_TIMEOUT_SECONDS = 60;

/**
 * Clamps the Sanity `globalSettings.checkoutTimeoutSeconds` value (admin-
 * configurable, see MLC-2522) to a safe range and converts it to ms. Falls
 * back to `DEFAULT_REQUEST_TIMEOUT_MS` when the CMS value is missing or
 * invalid, so a bad/absent CMS value never disables the timeout entirely.
 */
export function resolveCheckoutTimeoutMs(checkoutTimeoutSeconds?: number | null): number {
  if (!checkoutTimeoutSeconds || !Number.isFinite(checkoutTimeoutSeconds) || checkoutTimeoutSeconds <= 0) {
    return DEFAULT_REQUEST_TIMEOUT_MS;
  }
  return Math.min(checkoutTimeoutSeconds, MAX_CHECKOUT_TIMEOUT_SECONDS) * 1000;
}

export class FetchTimeoutError extends Error {
  code = "FETCH_TIMEOUT";

  constructor(message: string) {
    super(message);
    this.name = "FetchTimeoutError";
  }
}

class HttpClient {
  private baseUrl: string;
  private accessToken?: string;
  private slownessThreshold: number = 1000;
  private cachedResponseThreshold: number = 50;
  private enableLog?: boolean;
  private locale?: string;

  // Request interceptor function
  private requestInterceptor = (request: Request): Request => request;

  // Response interceptor function
  private responseInterceptor = (response: Response): Response => response;

  constructor(baseUrl: string, accessToken?: string, locale?: string, options: { enableLog?: boolean } = {}) {
    this.baseUrl = baseUrl;
    this.accessToken = accessToken;
    this.locale = locale;
    this.enableLog = options.enableLog ?? process.env.ENABLE_NETWORK_LOGGING !== "false";
  }

  protected setAccessToken(accessToken: string): void {
    this.accessToken = accessToken;
  }
  protected setLocale(locale: string): void {
    this.locale = locale;
  }

  // Set authentication headers if needed
  private setHeaders(headers: Record<string, string>): void {
    // You can implement your authentication logic here
    if (this.accessToken) {
      headers["Authorization"] = `Bearer ${this.accessToken}`;
    }
    if (this.locale) {
      const channel = getChannelId(this.locale);
      headers["channel"] = channel;
      headers["Saleor-Locale"] = this.locale;
    }

  }

  // Handle response performance
  private logResponsePerformance(response: Response, startTime: number, options?: { [key: string]: BodyInit | null | undefined }): Response {
    const endTime = Date.now();
    const duration = endTime - startTime;
    const statusCode = response.status;

    let queryFragment = null;
    if (typeof options?.body === "string" && options?.body) {
      queryFragment = options.body;
    }

    if (statusCode !== 200) {
      console.error(
        `❌ Error: ${response.url} returned status ${statusCode} and took ${duration} ms. For ${getOperationName(queryFragment)}`
      );
    } else if (duration > this.slownessThreshold) {
      const reqUrl = options?.url || response.url;

      console.warn(
        `🐢 Slow Response: ${duration} ms. For ${getOperationName(queryFragment)}`
      );
    } else if (duration < this.cachedResponseThreshold) {
        const reqUrl = options?.url || response.url;
  
        console.warn(
          `🐇 Rapid Response (Likely Cached): ${duration} ms. For ${getOperationName(queryFragment)}`
        );
      }

    return response;
  }

  private async performFetch<T>(url: string, options: RequestInit, cache?: TypeFetchCache, next?: NextOptions, timeoutMs?: number): Promise<T> {
    const startTime = Date.now();
    const request = this.requestInterceptor(new Request(url, options));

    const requestOptions: RequestInit = {
      method: request.method,
      headers: request.headers,
    };

    if (options.body) {
      requestOptions.body = options.body;
    }

    // Determine if we need to force cache
    const hasNextOptions = next && (next.revalidate !== undefined || (next.tags && next.tags.length > 0));

    //The force-cache is apparently required for caching to work for POST requests
    //https://github.com/vercel/next.js/issues/70946
    if (cache) {
      requestOptions.cache = cache;
    //} else if (hasNextOptions) {
    //  // Automatically force cache if revalidate or tags are passed
    //  requestOptions.cache = 'force-cache';
    } else if (!hasNextOptions) {
      // Default to no-store if nothing specified
      requestOptions.cache = 'no-store';
    }

    // Handle next options (revalidate, tags)
    if (hasNextOptions) {
      requestOptions.next = { ...next };
    }
          
    // Opt-in only: most calls here have no inherent upper bound on how long a
    // legitimately slow query may take, so no timeout is applied unless the
    // caller explicitly passes `timeoutMs`.
    const controller = timeoutMs ? new AbortController() : undefined;
    const timeoutId = controller ? setTimeout(() => controller.abort(), timeoutMs) : undefined;
    if (controller) {
      requestOptions.signal = controller.signal;
    }

    try {
      const response = await fetch(url, requestOptions);
      const interceptedResponse = this.responseInterceptor(response);

      if (this.enableLog) {
        this.logResponsePerformance(interceptedResponse, startTime, { url, body: requestOptions?.body });
      }

      if (interceptedResponse.status === 204) {
        return null as T;
      }

      // Attempt JSON parsing, logging parse failures
      try {
        return await interceptedResponse.json();
      } catch (error) {
        // A timeout that fires while the body is still being read surfaces
        // here too (not just from the outer fetch() call) — rewriting it to
        // a generic Error would stop the outer catch's AbortError check from
        // ever seeing it. Rethrow unchanged so that check still runs (see
        // PR #3248 review on the magrabi-storefront sibling).
        if ((error as { name?: string })?.name === "AbortError") {
          throw error;
        }
        console.error(`🛑 JSON Parse Error:${interceptedResponse?.status} Failed to parse JSON for ${url}. Error:`, error);
        throw new Error(`Failed to parse JSON response for ${url}`);
      }
    } catch (error) {
      // DOMException is the common case, but fetch implementations have been
      // observed throwing other AbortError-named shapes too — check `.name`
      // directly rather than gating on `instanceof DOMException` first, or a
      // real timeout can silently fall through to the generic Error path
      // below and then get rethrown across the Server Action boundary as an
      // opaque failure instead of a FetchTimeoutError (see PR #2087 review).
      if (timeoutMs && (error as { name?: string })?.name === "AbortError") {
        console.error(`⏱️ Request Timeout: Fetch from ${url} aborted after ${timeoutMs}ms`);
        throw new FetchTimeoutError(`Request to ${url} timed out after ${timeoutMs}ms`);
      }

      console.error(
        `❌ Request Error: Failed to fetch from ${url}: \nQuery/Mutation:${typeof requestOptions?.body === "string" ? requestOptions?.body : ""}. Error:`,
        error
      );
      throw new Error(`Failed to fetch from ${url}: ${error}`);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async get<T>(endpoint: string, params: Record<string, any> = {}, cache?: TypeFetchCache, next?: NextOptions, header?: Record<string, string>): Promise<T> {
    const url = new URL(endpoint, this.baseUrl);
    url.search = new URLSearchParams(params).toString();

    const headers: Record<string, string> = { ...header };
    this.setHeaders(headers);

    const options: RequestInit = {
      method: "GET",
      headers,
    };

    return this.performFetch<T>(url.toString(), options, cache, next);
  }

  async post<T>(endpoint: string, data: any, cache?: TypeFetchCache, next?: NextOptions, header?: Record<string, string>): Promise<T> {
    return this.postWithTimeout<T>(undefined, endpoint, data, cache, next, header);
  }

  /**
   * Same as `post`, but with a bounded AbortController timeout when
   * `timeoutMs` is given. A separate method (not a new trailing param on
   * `post`) so existing callers — and subclass overrides like SaleorClient's
   * `post` — are never affected. `timeoutMs` is the first param here
   * precisely so it never collides with whatever a subclass appends after
   * its own existing params (see MLC-2522). `post` delegates here with
   * `timeoutMs` undefined so header/body construction can't drift between
   * the two (see PR #2087 review).
   */
  async postWithTimeout<T>(timeoutMs: number | undefined, endpoint: string, data: any, cache?: TypeFetchCache, next?: NextOptions, header?: Record<string, string>): Promise<T> {
    const url = new URL(endpoint, this.baseUrl);

    const headers: Record<string, string> = {
      ...header,
      "Content-Type": "application/json",
    };
    this.setHeaders(headers);

    const options: RequestInit = {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    };

    return this.performFetch<T>(url.toString(), options, cache, next, timeoutMs);
  }

  async postFormData<T>(endpoint: string, data: any): Promise<T> {
    const url = new URL(endpoint, this.baseUrl);

    const headers: Record<string, string> = {};
    this.setHeaders(headers);
    const startTime = Date.now();

    const options: RequestInit = {
      method: "POST",
      headers,
      body: data,
    };

    const request = this.requestInterceptor(new Request(url, options));

    const requestOptions: RequestInit = {
      method: request.method,
      headers: options.headers,
    };

    if (options.body) {
      requestOptions.body = options.body;
    }

    const response: Response = await fetch(url.toString(), {
      ...requestOptions,
    });

    const interceptedResponse = this.responseInterceptor(response);

    if (this.enableLog) {
      this.logResponsePerformance(interceptedResponse, startTime);
    }

    if (interceptedResponse.status === 204) {
      return null as T;
    }

    if (!interceptedResponse.ok) {
      throw interceptedResponse;
    }

    return await interceptedResponse.json();
  }

  async put<T>(endpoint: string, data: any, cache?: TypeFetchCache, next?: NextOptions): Promise<T> {
    const url = new URL(endpoint, this.baseUrl);
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    this.setHeaders(headers);

    const options: RequestInit = {
      method: "PUT",
      headers,
      body: JSON.stringify(data),
    };

    return this.performFetch<T>(url.toString(), options, cache, next);
  }

  async patch<T>(endpoint: string, data: any, cache?: TypeFetchCache, next?: NextOptions, header?: Record<string, string>): Promise<T> {
    const url = new URL(endpoint, this.baseUrl);

    const headers: Record<string, string> = {
      ...header,
      "Content-Type": "application/json",
    };
    this.setHeaders(headers);

    const options: RequestInit = {
      method: "PATCH",
      headers,
      body: JSON.stringify(data),
    };

    return this.performFetch<T>(url.toString(), options, cache, next);
  }

  async delete<T>(endpoint: string, cache?: TypeFetchCache, next?: NextOptions): Promise<T | null> {
    const url = new URL(endpoint, this.baseUrl);

    const headers: Record<string, string> = {};
    this.setHeaders(headers);

    const options: RequestInit = {
      method: "DELETE",
      headers,
    };

    return this.performFetch<T>(url.toString(), options, cache, next);
  }
}

export default HttpClient;
